/**
 * @file index.tsx
 *
 * @fileoverview Renders a entity form with all fields provided to it
 * through a prop : entity definition. The main job of this component is to hold state and some logic to
 * the entity form.
 */

import { EntityDefinition } from '~/jobiqo/models/entity';
import { FieldGroupInfo } from '~/jobiqo/models/forms';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useEntityFormHelpers } from './hooks/useEntityForm';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FormActions } from '~/vcl/molecules/01-form/01-form-actions';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Alert } from '~/vcl/atoms/18-alert';
import FormGroupBox from './FormGroupBox';
import { useFieldErrors } from './hooks/useFieldErrors';

// Props needed by the form component.
export interface FormWidgetProps {
  /**
   * The name of the entity to be displayed (node, user, profile).
   */
  name: string;
  /**
   * The bundle of the entity to be displayed (job_per_templae, job_per_link).
   */
  bundle?: string;
  /**
   * The definition for all the fields in the entity.
   */
  definition: EntityDefinition;
  /**
   * Submit Label
   */
  submitLabel?: string | React.ReactNode;
  /**
   * If submit button should be always enabled.
   *
   * @default "false"
   */
  submitAlwaysEnabled?: boolean;
  /**
   * The submit handler for the form.
   */
  onSubmit?: (values: any) => any;
  /**
   *  Callback for when cancelling the form submission.
   */
  onCancel?: () => void;
  /**
   * Callback for when removing / deleting an entity.
   */
  onRemove?: () => void;
  /**
   * A list of excluded fields for the form being generated.
   */
  excludedFieldList?: string[];
  /**
   * A list of additional fields for the form.
   * These fields are passed to the Form component as
   * children and should be handled in the component referencing Form.
   */
  additionalFieldList?: string[];
  /**
   * The entity in the case of an edit form. If you need to run update on an edit form
   * make sure to pass an id to be used as key so that if you change the entity it knows
   * to update the form values.
   */
  entity?: any;
  /**
   * Information about how fields are organized in groups.
   */
  fieldGroups?: FieldGroupInfo[];
  /**
   * Render prop for custom elements.
   */
  overrides?: {
    [key: string]: ({ field, setFieldValue, values }) => void;
  };
  /**
   * Render prop for custom group elements.
   */
  overridesGroup?: {
    [key: string]: ({
      group,
      values,
      isValid,
      bundle,
      overrides,
      formGroupsChildren,
      name,
    }) => void;
  };
  /**
   * Extra field validation passed from the outside.
   */
  extraValidationSchema?: any;
  /**
   * The extra initial values passed from the outside.
   */
  extraInitialValues?: any;
  /**
   * The list of errors for the form.
   */
  fieldErrorList?: any[];
  /**
   * Custom render prop for buttons.
   */
  renderButtons?: ({
    isValid,
    isSubmitting,
    onFormCancel,
    onFormRemove,
    onFormSubmit,
  }) => React.ReactNode;
  /**
   * Children prop.
   */
  children?: React.ReactNode;
  /**
   * Form groups additional elements.
   */
  formGroupsChildren?: {
    [key: string]: ({ definition }) => React.ReactNode;
  };
  /**
   * The form id.
   */
  id?: string;
  /**
   * Callback for form onBlur.
   */
  onBlur?: () => void;
  /**
   * Callback for the form onFocus event.
   */
  onFocus?: () => void;
}

export const FormWidget = ({
  name,
  bundle,
  fieldGroups = [],
  excludedFieldList = [],
  entity,
  definition,
  submitLabel,
  submitAlwaysEnabled = false,
  onSubmit,
  onRemove,
  onCancel,
  overrides,
  overridesGroup,
  extraValidationSchema,
  renderButtons,
  children,
  formGroupsChildren,
  additionalFieldList = [],
  extraInitialValues,
  fieldErrorList,
  id,
  onBlur,
  onFocus,
}: FormWidgetProps) => {
  const [showRemoveDialog, setShowremoveDialog] = useState(false);
  const {
    getInitialValues,
    generateValidationSchema,
    processFields,
    orderFieldGroups,
  } = useEntityFormHelpers({
    fieldGroups,
    excludedFieldList,
    entity,
    definition,
    overrides,
    bundle,
    additionalFieldList,
    extraValidationSchema,
    extraInitialValues,
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(generateValidationSchema()),
    defaultValues: getInitialValues(),
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting, isValid },
    getValues,
  } = methods;

  // Read backend validation errors and set them in the form.
  useFieldErrors(fieldErrorList, setError);

  return (
    <FormProvider {...methods}>
      <form
        id={id}
        onSubmit={handleSubmit(() => {
          onSubmit?.(processFields(getValues()));
        })}
        onBlur={onBlur}
        onFocus={onFocus}
      >
        {orderFieldGroups().map((group) => {
          return overridesGroup?.[group.key] ? (
            overridesGroup?.[group.key]({
              group,
              values: getValues(),
              isValid,
              bundle,
              overrides,
              formGroupsChildren,
              name,
            })
          ) : (
            <FormGroupBox
              key={group.key}
              group={group}
              isValid={isValid}
              bundle={bundle}
              overrides={overrides}
              formGroupsChildren={formGroupsChildren}
              name={name}
              definition={definition}
            />
          );
        })}
        {children}
        <Spacing size="large" />
        {renderButtons &&
          renderButtons({
            isValid,
            isSubmitting,
            onFormCancel: onCancel,
            onFormRemove: onRemove,
            onFormSubmit: onSubmit,
          })}
        {!renderButtons && (
          <FormActions>
            {onCancel && (
              <Button
                type="button"
                buttonStyle="secondary"
                raised={false}
                onClick={() => onCancel?.()}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            )}
            {onRemove && (
              <>
                <Button
                  type="button"
                  buttonStyle="danger"
                  data-test-id="ef-delete"
                  onClick={(e) => {
                    setShowremoveDialog(true);
                  }}
                >
                  <FormattedMessage id="delete" defaultMessage="Delete" />
                </Button>
                <Alert
                  isShown={showRemoveDialog}
                  onConfirm={() => {
                    setShowremoveDialog(false);
                    onRemove?.();
                  }}
                  onClose={() => setShowremoveDialog(false)}
                  confirmText={
                    <FormattedMessage
                      id="accept_deletion"
                      defaultMessage="Yes, delete"
                    />
                  }
                  cancelText={
                    <FormattedMessage id="cancel" defaultMessage="Cancel" />
                  }
                  alertLabel={
                    <FormattedMessage id="delete" defaultMessage="Delete" />
                  }
                >
                  <FormattedMessage
                    id="delete_dialog_text"
                    defaultMessage="Are you sure you want to delete this item?"
                  />
                </Alert>
              </>
            )}
            <Button
              disabled={!submitAlwaysEnabled && (isSubmitting || !isValid)}
              buttonStyle="primary"
              data-test-id="ef-submit"
              type="submit"
            >
              {submitLabel ? (
                submitLabel
              ) : (
                <FormattedMessage id="submit" defaultMessage="Submit" />
              )}
            </Button>
          </FormActions>
        )}
      </form>
    </FormProvider>
  );
};
export default FormWidget;
